import React from 'react'
import { createPortal } from 'react-dom'
import { AlertTriangle, X } from 'lucide-react'
import { cn } from '../../lib/utils'
import { tx } from '../../lib/i18n'

interface ConfirmDialogProps {
  open: boolean
  onClose: () => void
  onConfirm: () => void
  title: string
  description: string
  confirmLabel?: string
  variant?: 'danger' | 'warning' | 'default'
  requireReason?: boolean
  reason?: string
  onReasonChange?: (reason: string) => void
  isLoading?: boolean
}

const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  open,
  onClose,
  onConfirm,
  title,
  description,
  confirmLabel = tx('Confirm'),
  variant = 'default',
  requireReason = false,
  reason = '',
  onReasonChange,
  isLoading = false,
}) => {
  if (!open) return null

  const confirmBtnClass = {
    danger: 'bg-red-600 hover:bg-red-700 text-white',
    warning: 'bg-amber-500 hover:bg-amber-600 text-white',
    default: 'bg-indigo-600 hover:bg-indigo-700 text-white',
  }[variant]

  const iconClass = {
    danger: 'text-red-500 bg-red-100',
    warning: 'text-amber-500 bg-amber-100',
    default: 'text-indigo-500 bg-indigo-100',
  }[variant]

  const isConfirmDisabled = isLoading || (requireReason && !reason.trim())

  // Rendered into <body> so no ancestor (transform / overflow / stacking context) can clip or tile the
  // backdrop; plain translucent overlay (backdrop-blur caused repaint artifacts in Chrome).
  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center" role="dialog" aria-modal="true">
      <div className="fixed inset-0 bg-gray-900/50" onClick={isLoading ? undefined : onClose} />
      <div className="relative z-10 bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 p-6">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
        >
          <X className="h-5 w-5" />
        </button>
        <div className="flex items-start gap-4">
          <div className={cn('p-3 rounded-xl flex-shrink-0', iconClass)}>
            <AlertTriangle className="h-5 w-5" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-base font-semibold text-gray-900">{title}</h3>
            <p className="mt-1 text-sm text-gray-500">{description}</p>
          </div>
        </div>

        {requireReason && (
          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {tx('Reason')} <span className="text-red-500">*</span>
            </label>
            <textarea
              rows={3}
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none"
              placeholder={tx('Please provide a reason...')}
              value={reason}
              onChange={(e) => onReasonChange?.(e.target.value)}
            />
          </div>
        )}

        <div className="mt-6 flex gap-3 justify-end">
          <button
            onClick={onClose}
            disabled={isLoading}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            {tx('Cancel')}
          </button>
          <button
            onClick={onConfirm}
            disabled={isConfirmDisabled}
            className={cn(
              'px-4 py-2 text-sm font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2',
              confirmBtnClass
            )}
          >
            {isLoading && (
              <span className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            )}
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  )
}

export default ConfirmDialog
